/**
 * CanvasPanel.jsx — slide-in Artifacts / Canvas panel.
 *
 * Opens as a right-side overlay when a user clicks "Open in Canvas" on an
 * assistant message that contains an artifact. Modes:
 *   • HTML/SVG → live sandboxed iframe preview + Code tab
 *   • Markdown → rendered on the left, source on the right
 *   • Code → syntax-lite pre with copy & download
 *
 * Sandbox note: HTML previews run inside `<iframe sandbox="allow-scripts">`.
 * Same-origin is deliberately NOT granted — user-authored scripts cannot
 * touch cookies, localStorage, or the parent window.
 */
import React, { useMemo, useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import * as L from "lucide-react";
import api from "@/lib/api";

const EXT_BY_LANG = {
  html: "html", svg: "svg", md: "md", markdown: "md",
  python: "py", javascript: "js", typescript: "ts", tsx: "tsx", jsx: "jsx",
  json: "json", css: "css", bash: "sh", shell: "sh", sh: "sh",
};

export default function CanvasPanel({ artifact, onClose }) {
  const [tab, setTab] = useState(artifact.kind === "code" ? "code" : "preview");
  const iframeRef = useRef(null);
  // Local override of artifact.code so refine loops can mutate in place
  // without needing to round-trip through the parent's message list.
  const [refinedCode, setRefinedCode] = useState(null);
  const [refineInput, setRefineInput] = useState("");
  const [refining, setRefining] = useState(false);
  // Auto-critique state — runs once when the panel opens for an HTML artifact.
  // Shape: { status: "idle"|"running"|"done"|"error", score?, one_liner?, needs_revision?, revised_html?, top_issues?[] }
  const [critique, setCritique] = useState({ status: "idle" });
  const [critiqueOpen, setCritiqueOpen] = useState(false);
  // Custom image generation state — tracks how many rammy-gen:// URLs we're
  // resolving into real base64 data URLs from Nano Banana.
  // { total, done, active, imageMap: { "rammy-gen://…": "data:image/png;base64,…" } }
  const [imgGen, setImgGen] = useState({ total: 0, done: 0, active: false, imageMap: {} });
  // Publish state: turns the finished artifact into a shareable public URL.
  // Once published, we cache the URL locally so re-clicks show the existing
  // link instead of minting a fresh slug on every click.
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null); // { slug, public_url, title }
  const [publishOpen, setPublishOpen] = useState(false);
  const activeCode = useMemo(() => {
    const base = refinedCode ?? artifact.code;
    if (!imgGen.imageMap || Object.keys(imgGen.imageMap).length === 0) return base;
    // Swap every resolved rammy-gen:// URL with its real data URL in one pass.
    let out = base;
    for (const [orig, dataUrl] of Object.entries(imgGen.imageMap)) {
      // Escape regex specials
      const re = new RegExp(orig.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
      out = out.replace(re, dataUrl);
    }
    return out;
  }, [artifact.code, refinedCode, imgGen.imageMap]);

  // Reset tab AND refined code when the artifact changes (new one opened).
  useEffect(() => {
    setTab(artifact.kind === "code" ? "code" : "preview");
    setRefinedCode(null);
    setRefineInput("");
    setCritique({ status: "idle" });
    setCritiqueOpen(false);
    setImgGen({ total: 0, done: 0, active: false, imageMap: {} });
    setPublished(null);
    setPublishOpen(false);
  }, [artifact.id]);

  // ── rammy-gen:// hero-image resolution ─────────────────────────────
  // Scan the artifact for `rammy-gen://<prompt>?ar=hero` URLs. For each
  // UNIQUE URL, hit /api/artifacts/generate-image and swap the returned
  // data URL back into `activeCode` via `imgGen.imageMap`. Runs in
  // parallel, fault-tolerant — a single failing image doesn't block others.
  useEffect(() => {
    if (artifact.kind !== "html") return;
    const source = artifact.code || "";
    const matches = [...source.matchAll(/rammy-gen:\/\/[^\s"'<>]+/g)].map((m) => m[0]);
    const uniques = [...new Set(matches)];
    if (uniques.length === 0) return;
    let cancelled = false;
    setImgGen({ total: uniques.length, done: 0, active: true, imageMap: {} });

    (async () => {
      // Parse each rammy-gen URL into { prompt, aspect } client-side so we
      // don't need a separate parser round-trip. Match backend contract.
      const parsed = uniques.map((raw) => {
        const rest = raw.replace(/^rammy-gen:\/\//, "");
        const [pathPart, queryPart] = rest.split("?");
        let aspect = "wide";
        if (queryPart) {
          const p = new URLSearchParams(queryPart);
          if (p.get("ar")) aspect = p.get("ar");
        }
        let prompt = "";
        try {
          prompt = decodeURIComponent(pathPart.replace(/\+/g, " "));
        } catch (_) {
          prompt = pathPart.replace(/\+/g, " ");
        }
        return { original: raw, prompt: prompt.trim().slice(0, 400), aspect };
      });

      await Promise.all(parsed.map(async (p) => {
        if (cancelled) return;
        try {
          const { data } = await api.post("/artifacts/generate-image", {
            prompt: p.prompt,
            aspect: p.aspect,
          }, { timeout: 90000 });
          if (cancelled) return;
          setImgGen((s) => ({
            ...s,
            done: s.done + 1,
            imageMap: { ...s.imageMap, [p.original]: data.data_url },
          }));
        } catch (e) {
          if (cancelled) return;
          // Failed image → keep the placeholder alt behavior; just increment
          // done so the progress indicator finishes.
          setImgGen((s) => ({ ...s, done: s.done + 1 }));
          console.warn("rammy-gen image failed:", p.prompt, e?.message);
        }
      }));
      if (!cancelled) setImgGen((s) => ({ ...s, active: false }));
    })();

    return () => { cancelled = true; };
  }, [artifact.id, artifact.kind, artifact.code]);

  // Auto-critique: only for HTML artifacts, only once per artifact, only
  // if the artifact is large enough to be a "real" site (not a snippet).
  // We fire this silently in the background — the user sees a small
  // "auditing…" pip in the header that transitions to either a green
  // "passes audit" tick or a shiny "v2 available" button.
  useEffect(() => {
    if (artifact.kind !== "html") return;
    if ((artifact.code || "").length < 800) return;  // snippet, not a site
    if (critique.status !== "idle") return;
    let cancelled = false;
    (async () => {
      setCritique({ status: "running" });
      try {
        const { data } = await api.post("/artifacts/critique", {
          current_html: artifact.code,
          hint: artifact.title || "",
        }, { timeout: 120000 });
        if (cancelled) return;
        setCritique({
          status: "done",
          score: data.score,
          one_liner: data.one_liner,
          needs_revision: data.needs_revision,
          revised_html: data.revised_html,
          top_issues: data.top_issues || [],
          error: data.error,
        });
      } catch (e) {
        if (cancelled) return;
        setCritique({ status: "error", error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [artifact.id, artifact.kind]);

  const applyRevisedV2 = () => {
    if (!critique.revised_html) return;
    setRefinedCode(critique.revised_html);
    toast.success("v2 applied — Rammy's stronger revision is now live");
    setCritiqueOpen(false);
  };

  // Publish: send the fully-baked activeCode (with resolved base64 images)
  // to /api/artifacts/publish and get back a public URL anyone can visit.
  const publish = async () => {
    if (publishing) return;
    // Only meaningful for HTML — SVG/markdown/code aren't "sites".
    if (artifact.kind !== "html") {
      toast.error("Only HTML sites can be published");
      return;
    }
    setPublishing(true);
    try {
      const { data } = await api.post("/artifacts/publish", {
        html: activeCode,
        title: artifact.title || "Untitled site",
      }, { timeout: 30000 });
      setPublished(data);
      setPublishOpen(true);
      toast.success("Published — share the link");
      // Publishing = strongest implicit approval. Silent DNA learn from
      // the finished HTML (stripped of markup below).
      try {
        const plain = activeCode
          .replace(/<script[\s\S]*?<\/script>/gi, "")
          .replace(/<style[\s\S]*?<\/style>/gi, "")
          .replace(/<[^>]+>/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        if (plain.length >= 200) {
          api.post("/dna/learn", {
            sample: plain.slice(0, 12000),
            source: "published_artifact",
          }).catch(() => { /* silent */ });
        }
      } catch { /* never break the publish flow on a learn error */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't publish. Try again");
    } finally {
      setPublishing(false);
    }
  };

  const copyPublishedUrl = async () => {
    if (!published?.public_url) return;
    try {
      await navigator.clipboard.writeText(published.public_url);
      toast.success("Link copied");
    } catch (_) {
      toast.error("Couldn't copy");
    }
  };

  // Rebuild the iframe srcDoc whenever the artifact code changes
  const iframeSrcDoc = useMemo(() => {
    if (artifact.kind === "html") {
      // Wrap fragments in a basic doc so relative body/head expectations work.
      if (/^\s*<!doctype/i.test(activeCode) || /^\s*<html[\s>]/i.test(activeCode)) {
        return activeCode;
      }
      return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"/><style>body{font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;padding:1rem;color:#111;background:#faf7f2;margin:0}</style></head><body>${activeCode}</body></html>`;
    }
    if (artifact.kind === "svg") {
      return `<!doctype html><html><head><meta charset="utf-8"/><style>html,body{margin:0;height:100%;background:#faf7f2;display:flex;align-items:center;justify-content:center;padding:1rem}svg{max-width:100%;max-height:100%}</style></head><body>${activeCode}</body></html>`;
    }
    return null;
  }, [artifact.id, artifact.kind, activeCode]);

  const renderedMarkdown = useMemo(() => {
    if (artifact.kind !== "markdown") return null;
    return renderMarkdown(activeCode);
  }, [artifact.id, artifact.kind, activeCode]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(activeCode);
      toast.success("Code copied");
    } catch (_) {
      toast.error("Couldn't copy");
    }
  };

  const downloadCode = () => {
    const ext = EXT_BY_LANG[artifact.lang] || EXT_BY_LANG[artifact.kind] || "txt";
    const blob = new Blob([activeCode], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rammy-artifact.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  // Refine: only meaningful for HTML artifacts. Sends current code + a
  // natural-language instruction to /api/artifacts/refine.
  const canRefine = artifact.kind === "html";
  const refine = async () => {
    const instruction = refineInput.trim();
    if (!instruction || refining) return;
    setRefining(true);
    try {
      const { data } = await api.post("/artifacts/refine", {
        current_html: activeCode,
        instruction,
      }, { timeout: 90000 });
      setRefinedCode(data.html);
      setRefineInput("");
      toast.success("Updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refine failed — try again");
    } finally {
      setRefining(false);
    }
  };

  const openInNewTab = () => {
    if (!iframeSrcDoc) return;
    const w = window.open("", "_blank", "noopener,noreferrer");
    if (!w) { toast.error("Popup blocked"); return; }
    w.document.open();
    w.document.write(iframeSrcDoc);
    w.document.close();
  };

  const hasPreview = artifact.kind === "html" || artifact.kind === "svg" || artifact.kind === "markdown";

  return (
    <div
      className="fixed inset-y-0 right-0 w-full md:w-[560px] lg:w-[640px] xl:w-[720px] bg-[var(--rm-surface)] border-l border-[var(--rammy-border)] shadow-[-24px_0_60px_rgba(17,19,24,0.12)] z-40 flex flex-col animate-in slide-in-from-right duration-300"
      data-testid="canvas-panel"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-[var(--rammy-border)] shrink-0">
        <div className="w-8 h-8 rounded-xl bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] flex items-center justify-center">
          <KindIcon kind={artifact.kind} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">Canvas</div>
          <div className="text-sm font-medium truncate" data-testid="canvas-title">{artifact.title}</div>
        </div>
        <ImgGenPill imgGen={imgGen}/>
        <CritiquePill critique={critique} onOpen={() => setCritiqueOpen((v) => !v)} isOpen={critiqueOpen}/>
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-muted)]"
          data-testid="canvas-close"
          aria-label="Close canvas"
        >
          <L.X size={16}/>
        </button>
      </div>

      {/* Critique detail — expanded state */}
      {critiqueOpen && critique.status === "done" && (
        <CritiqueDetailPanel
          critique={critique}
          onApply={applyRevisedV2}
          onClose={() => setCritiqueOpen(false)}
          isV2Applied={refinedCode !== null && refinedCode === critique.revised_html}
        />
      )}

      {/* Tabs */}
      {hasPreview && (
        <div className="px-5 pt-3 pb-2 border-b border-[var(--rammy-border)] flex items-center gap-2 shrink-0">
          <TabButton label="Preview" active={tab === "preview"} onClick={() => setTab("preview")} testid="canvas-tab-preview"/>
          <TabButton label="Code"    active={tab === "code"}    onClick={() => setTab("code")}    testid="canvas-tab-code"/>
          <div className="flex-1"/>
          {(artifact.kind === "html" || artifact.kind === "svg") && tab === "preview" && (
            <button
              onClick={openInNewTab}
              className="text-[11px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] transition inline-flex items-center gap-1"
              data-testid="canvas-open-new-tab"
            >
              <L.ExternalLink size={11}/> New tab
            </button>
          )}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === "preview" && artifact.kind === "html" && (
          <iframe
            ref={iframeRef}
            title={artifact.title}
            sandbox="allow-scripts allow-forms"
            className="w-full h-full border-0 bg-[var(--rammy-bg)]"
            srcDoc={iframeSrcDoc}
            data-testid="canvas-html-preview"
          />
        )}
        {tab === "preview" && artifact.kind === "svg" && (
          <iframe
            title={artifact.title}
            sandbox=""
            className="w-full h-full border-0 bg-[var(--rammy-bg)]"
            srcDoc={iframeSrcDoc}
            data-testid="canvas-svg-preview"
          />
        )}
        {tab === "preview" && artifact.kind === "markdown" && (
          <div
            className="h-full overflow-y-auto subtle-scroll px-6 py-5 prose prose-sm max-w-none prose-headings:font-display prose-headings:text-[var(--rammy-text)] prose-p:text-[var(--rammy-text-sec)] prose-code:text-[var(--rammy-cyan-dark)] prose-a:text-[var(--rammy-cyan-dark)]"
            data-testid="canvas-md-preview"
            dangerouslySetInnerHTML={{ __html: renderedMarkdown }}
          />
        )}
        {(tab === "code" || !hasPreview) && (
          <pre
            className="h-full overflow-auto subtle-scroll m-0 px-5 py-4 text-[12.5px] leading-relaxed font-mono bg-[var(--rm-ink-900)] text-[#e6f7f3] whitespace-pre"
            data-testid="canvas-code"
          >
            <code>{activeCode}</code>
          </pre>
        )}
      </div>

      {/* Refine bar — only for HTML artifacts. Lets the user tweak in place. */}
      {canRefine && (
        <div className="px-5 py-2.5 border-t border-[var(--rammy-border)] shrink-0 bg-[var(--rammy-surface-alt)]/40">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] flex items-center justify-center shrink-0">
              <L.Wand2 size={12}/>
            </div>
            <input
              value={refineInput}
              onChange={(e) => setRefineInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); refine(); } }}
              placeholder={refining ? "Refining…" : "Refine: make hero bolder, add stats section, try dark mode…"}
              disabled={refining}
              maxLength={500}
              className="flex-1 h-8 bg-transparent border-0 outline-none text-[12.5px] placeholder:text-[var(--rammy-text-muted)] text-[var(--rammy-text)]"
              data-testid="canvas-refine-input"
            />
            <button
              onClick={refine}
              disabled={!refineInput.trim() || refining}
              className="h-8 px-3 rounded-full bg-[var(--rm-ink-900)] text-white text-[11px] font-medium disabled:opacity-40 hover:opacity-90 transition inline-flex items-center gap-1"
              data-testid="canvas-refine-btn"
            >
              {refining ? <L.Loader2 size={11} className="animate-spin"/> : <L.ArrowUp size={11}/>}
              {refining ? "Working" : "Refine"}
            </button>
          </div>
          {refinedCode !== null && (
            <div className="mt-1.5 text-[10px] text-[var(--rammy-text-muted)] flex items-center gap-2">
              <span className="inline-flex items-center gap-1 text-[var(--rammy-cyan-dark)]">
                <L.Sparkles size={10}/> Refined version showing
              </span>
              <button
                onClick={() => setRefinedCode(null)}
                className="underline hover:text-[var(--rammy-text)]"
                data-testid="canvas-refine-revert"
              >
                Revert to original
              </button>
            </div>
          )}
        </div>
      )}

      {/* Footer actions */}
      <div className="relative px-5 py-3 border-t border-[var(--rammy-border)] flex items-center gap-2 shrink-0">
        <div className="text-[11px] text-[var(--rammy-text-muted)] flex-1 truncate">
          {activeCode.length.toLocaleString()} characters · {artifact.lang || "text"}
        </div>
        <button
          onClick={copyCode}
          className="btn-pill h-8 text-xs px-3 border border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] inline-flex items-center gap-1.5"
          data-testid="canvas-copy"
        >
          <L.Copy size={12}/> Copy
        </button>
        <button
          onClick={downloadCode}
          className="btn-pill h-8 text-xs px-3 border border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] inline-flex items-center gap-1.5"
          data-testid="canvas-download"
        >
          <L.Download size={12}/> Download
        </button>
        {artifact.kind === "html" && (
          <button
            onClick={published ? () => setPublishOpen((v) => !v) : publish}
            disabled={publishing}
            className={`btn-pill h-8 text-xs px-3 inline-flex items-center gap-1.5 transition ${
              published
                ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                : "bg-[var(--rm-ink-900)] text-white hover:opacity-90"
            }`}
            data-testid="canvas-publish"
          >
            {publishing ? (
              <><L.Loader2 size={12} className="animate-spin"/> Publishing…</>
            ) : published ? (
              <><L.Globe size={12}/> Published</>
            ) : (
              <><L.Send size={12}/> Publish</>
            )}
          </button>
        )}

        {/* Published popover — a compact card that reveals the shareable URL */}
        {publishOpen && published && (
          <PublishedPopover
            published={published}
            onCopy={copyPublishedUrl}
            onClose={() => setPublishOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

function TabButton({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
        active
          ? "bg-[var(--rm-ink-900)] text-white"
          : "text-[var(--rammy-text-sec)] hover:bg-[var(--rammy-surface-alt)]"
      }`}
    >
      {label}
    </button>
  );
}

function KindIcon({ kind }) {
  const map = {
    html: L.Code2,
    svg: L.Shapes,
    markdown: L.FileText,
    code: L.FileCode2,
  };
  const Icon = map[kind] || L.FileCode2;
  return <Icon size={15}/>;
}

/**
 * PublishedPopover — floating card anchored above the Publish button that
 * reveals the shareable URL, Copy + Open actions, and a friendly one-liner.
 */
function PublishedPopover({ published, onCopy, onClose }) {
  return (
    <div
      className="absolute bottom-14 right-5 w-[340px] rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] shadow-[0_24px_60px_rgba(17,19,24,0.18)] p-4 z-10 animate-in fade-in slide-in-from-bottom-2 duration-200"
      data-testid="canvas-published-popover"
      role="dialog"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
          <L.Globe size={16}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-800">Live on the web</div>
          <div className="text-[13px] font-medium text-[var(--rammy-text)] mt-0.5 truncate">
            {published.title}
          </div>
          <div className="mt-2 text-[11px] text-[var(--rammy-text-muted)] leading-snug">
            Anyone with this link can view the site. Refresh it later to see updates.
          </div>
          <div
            className="mt-3 flex items-center gap-1 rounded-lg border border-[var(--rammy-border)] bg-[var(--rammy-surface-alt)]/60 px-2.5 py-1.5"
          >
            <span className="text-[11px] font-mono text-[var(--rammy-text)] truncate flex-1" data-testid="canvas-published-url">
              {published.public_url}
            </span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={onCopy}
              className="h-8 px-3 rounded-full bg-[var(--rm-ink-900)] text-white text-[11px] font-semibold hover:opacity-90 inline-flex items-center gap-1.5"
              data-testid="canvas-published-copy"
            >
              <L.Copy size={11}/> Copy link
            </button>
            <a
              href={published.public_url}
              target="_blank"
              rel="noopener noreferrer"
              className="h-8 px-3 rounded-full border border-[var(--rammy-border)] text-[var(--rammy-text)] text-[11px] font-semibold hover:border-[var(--rammy-cyan)] inline-flex items-center gap-1.5"
              data-testid="canvas-published-open"
            >
              <L.ExternalLink size={11}/> Open
            </a>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-full hover:bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-muted)]"
          aria-label="Close"
        >
          <L.X size={13}/>
        </button>
      </div>
    </div>
  );
}

/**
 * ImgGenPill — status chip for background Nano Banana hero-image generation.
 * Shows "Painting hero 1/3" while active, then a subtle "3 custom images"
 * confirmation after all resolve. Silently disappears if no rammy-gen://
 * URLs were in the artifact.
 */
function ImgGenPill({ imgGen }) {
  if (!imgGen || imgGen.total === 0) return null;
  if (imgGen.active) {
    return (
      <div
        className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-gradient-to-r from-rose-100 to-amber-100 text-rose-900 text-[11px] font-medium"
        data-testid="canvas-imggen-running"
        title="Nano Banana is painting custom hero imagery"
      >
        <L.Palette size={11} className="animate-pulse"/>
        Painting {imgGen.done + 1}/{imgGen.total}
      </div>
    );
  }
  return (
    <div
      className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-amber-100 text-amber-800 text-[11px] font-medium"
      data-testid="canvas-imggen-done"
      title="Custom hero images generated"
    >
      <L.Palette size={11}/>
      {imgGen.total} custom image{imgGen.total === 1 ? "" : "s"}
    </div>
  );
}

/**
 * CritiquePill — the compact indicator in the Canvas header that shows the
 * status of the background audit. Idle / running / done+passes / done+v2 /
 * error. Clickable when there's something to reveal.
 */
function CritiquePill({ critique, onOpen, isOpen }) {
  if (critique.status === "idle") return null;
  if (critique.status === "running") {
    return (
      <div
        className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-[var(--rammy-cyan)]/10 text-[var(--rammy-cyan-dark)] text-[11px] font-medium"
        data-testid="canvas-critique-running"
        title="Rammy is auditing this site"
      >
        <L.Loader2 size={11} className="animate-spin"/>
        Auditing
      </div>
    );
  }
  if (critique.status === "error") {
    return (
      <div className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-[var(--rammy-text-muted)]/10 text-[var(--rammy-text-muted)] text-[11px]"
        data-testid="canvas-critique-error" title="Audit unavailable">
        <L.AlertCircle size={11}/> Audit off
      </div>
    );
  }
  // status === "done"
  if (critique.needs_revision && critique.revised_html) {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-expanded={isOpen}
        className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-gradient-to-r from-[var(--rammy-cyan)] to-emerald-400 text-[var(--rammy-text)] text-[11px] font-semibold shadow-[0_6px_20px_-6px_var(--rammy-cyan)] hover:brightness-105 transition"
        data-testid="canvas-critique-v2-cta"
      >
        <L.Sparkles size={11}/>
        v2 ready
        {typeof critique.score === "number" && (
          <span className="opacity-70 font-normal">· v1 scored {critique.score}/10</span>
        )}
      </button>
    );
  }
  // Passes audit — quiet green tick
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-expanded={isOpen}
      className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-medium hover:bg-emerald-200 transition"
      data-testid="canvas-critique-pass"
      title={critique.one_liner || "Passes design audit"}
    >
      <L.CheckCircle2 size={11}/>
      {typeof critique.score === "number" ? `${critique.score}/10` : "Passes"}
    </button>
  );
}

/**
 * CritiqueDetailPanel — the expanded strip that appears below the header
 * when the user clicks the CritiquePill. Shows the auditor's verdict, the
 * top issues found, and — if a stronger v2 is available — an Apply button.
 */
function CritiqueDetailPanel({ critique, onApply, onClose, isV2Applied }) {
  const sevColor = {
    high: "bg-rose-100 text-rose-800",
    med:  "bg-amber-100 text-amber-800",
    low:  "bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)]",
  };
  return (
    <div
      className="px-5 py-3 border-b border-[var(--rammy-border)] bg-gradient-to-br from-[var(--rammy-cyan)]/8 to-transparent shrink-0 animate-in slide-in-from-top duration-200"
      data-testid="canvas-critique-panel"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-[var(--rm-ink-900)] text-white flex items-center justify-center shrink-0">
          <L.Wand2 size={14}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">
            Rammy&apos;s design audit
          </div>
          <div className="text-[13px] font-medium text-[var(--rammy-text)] mt-0.5" data-testid="canvas-critique-verdict">
            {critique.one_liner || (critique.needs_revision ? "Stronger version drafted." : "This ships as-is.")}
          </div>
          {(critique.top_issues || []).length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="canvas-critique-issues">
              {critique.top_issues.slice(0, 4).map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] text-[var(--rammy-text-sec)]">
                  <span className={`shrink-0 inline-flex h-4 px-1.5 items-center rounded text-[9px] font-semibold uppercase tracking-wide ${sevColor[it.severity] || sevColor.low}`}>
                    {it.severity || "low"}
                  </span>
                  <span className="leading-snug">{it.note}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {critique.needs_revision && critique.revised_html && !isV2Applied && (
            <button
              onClick={onApply}
              className="h-8 px-3.5 rounded-full bg-[var(--rm-ink-900)] text-white text-[12px] font-semibold hover:opacity-90 inline-flex items-center gap-1.5 shadow-[0_10px_28px_-12px_rgba(17,19,24,0.5)]"
              data-testid="canvas-critique-apply"
            >
              <L.Sparkles size={12}/> Apply v2
            </button>
          )}
          {isV2Applied && (
            <span className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold">
              <L.CheckCircle2 size={11}/> v2 applied
            </span>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-[var(--rm-surface)]/60 text-[var(--rammy-text-muted)]"
            aria-label="Close audit panel"
          >
            <L.X size={13}/>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tiny markdown renderer — no dependency, just enough for headings, bold,
 * italics, inline code, code fences, lists, links, and paragraphs. HTML in
 * the source is escaped so untrusted markdown can't inject scripts.
 */
function renderMarkdown(src) {
  const esc = (s) => s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  // First, extract fenced code blocks so their content isn't mangled by
  // inline rules below.
  const codeBlocks = [];
  let s = src.replace(/```([a-zA-Z0-9]*)\n([\s\S]*?)```/g, (_, lang, code) => {
    codeBlocks.push({ lang, code });
    return `\uE000${codeBlocks.length - 1}\uE001`;
  });

  s = esc(s);
  // Headings
  s = s.replace(/^###### (.*)$/gm, "<h6>$1</h6>");
  s = s.replace(/^##### (.*)$/gm, "<h5>$1</h5>");
  s = s.replace(/^#### (.*)$/gm, "<h4>$1</h4>");
  s = s.replace(/^### (.*)$/gm, "<h3>$1</h3>");
  s = s.replace(/^## (.*)$/gm, "<h2>$1</h2>");
  s = s.replace(/^# (.*)$/gm, "<h1>$1</h1>");
  // Bold + italic
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  s = s.replace(/_([^_]+)_/g, "<em>$1</em>");
  // Inline code
  s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
  // Links
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  // Lists
  s = s.replace(/^(?:- |\* )(.*)$/gm, "<li>$1</li>");
  s = s.replace(/(<li>[\s\S]*?<\/li>)/g, "<ul>$1</ul>");
  s = s.replace(/<\/ul>\s*<ul>/g, ""); // merge adjacent lists
  // Paragraphs — any remaining non-empty line becomes a <p>
  s = s.split(/\n{2,}/).map(block => {
    if (/^<(h\d|ul|ol|pre|blockquote|div)/.test(block.trim())) return block;
    if (!block.trim()) return "";
    return `<p>${block.replace(/\n/g, "<br/>")}</p>`;
  }).join("\n");

  // Re-inject code blocks
  s = s.replace(/\uE000(\d+)\uE001/g, (_, i) => {
    const b = codeBlocks[Number(i)];
    return `<pre><code class="lang-${esc(b.lang)}">${esc(b.code)}</code></pre>`;
  });
  return s;
}
